import { APP_API_URL } from "@/consts";
import graphqlClient from "@/GQLClient";
import {
  type EntityPagerFilter,
  Delete_Type,
  GetSummarizedMealsOnMyInstitutionDocument,
  type SummarizedMealsFragment,
  type MealUnion,
  GetInternalMealsOnMyInstitutionDocument,
  type MealInput,
  SaveMealDocument,
  DeleteMealDocument,
  GetSummarizedExternalMealsDocument,
  Module_Type,
  GetExternalMealsOnMyInstitutionDocument,
  type MealFragment,
} from "@/graphql/generated-types";
import SentryGraphQLError from "@/helpers/exceptions/SentryGraphQLError";
import type { IPagerResult } from "@/interfaces";
import type { IdType } from "@/types";

const getMeals = async (
  filter: EntityPagerFilter,
): Promise<IPagerResult<SummarizedMealsFragment>> => {
  const response = await graphqlClient.query({
    query: GetSummarizedMealsOnMyInstitutionDocument,
    variables: {
      filter,
    },
  });

  if (response.data.institutions?.getMyInstitution.meals) {
    const { edges, pageInfo } =
      response.data.institutions.getMyInstitution.meals;
    return {
      edges: edges.map((item) => item.node as SummarizedMealsFragment),
      pageInfo,
    };
  }

  throw new SentryGraphQLError();
};

const getMealDetail = async (id: IdType) => {
  const ids: string[] = [];

  if (id) {
    ids.push(id.toString());
  }

  const response = await graphqlClient.query({
    query: GetInternalMealsOnMyInstitutionDocument,
    variables: {
      filter: {
        first: 1,
        ids,
      },
    },
  });

  if (response.data.institutions?.getMyInstitution.meals) {
    const { edges } = response.data.institutions.getMyInstitution.meals;
    return edges[0]?.node as MealFragment;
  }

  throw new SentryGraphQLError();
};

const getExternalMeals = async (
  filter: EntityPagerFilter,
): Promise<IPagerResult<MealUnion>> => {
  const response = await graphqlClient.query({
    query: GetSummarizedExternalMealsDocument,
    variables: {
      filter,
      type: Module_Type.Meal,
    },
  });

  if (response.data.institutions?.getMyInstitution.externalContent) {
    const { edges, pageInfo } =
      response.data.institutions.getMyInstitution.externalContent;
    return {
      edges: edges.map((item) => item.node as MealUnion),
      pageInfo,
    };
  }

  throw new SentryGraphQLError();
};

const getExternalMealDetail = async (id: IdType) => {
  const ids: string[] = [];

  if (id) {
    ids.push(id.toString());
  }

  const response = await graphqlClient.query({
    query: GetExternalMealsOnMyInstitutionDocument,
    variables: {
      type: Module_Type.Meal,
      filter: {
        first: 1,
        ids,
      },
    },
  });

  if (response.data.institutions?.getMyInstitution.externalContent) {
    const { edges } =
      response.data.institutions.getMyInstitution.externalContent;
    return edges[0]?.node as MealFragment;
  }

  throw new SentryGraphQLError();
};

const save = async (meal: MealInput, dishes: string[]) => {
  const response = await graphqlClient.mutate({
    mutation: SaveMealDocument,
    variables: {
      meal,
      dishes,
    },
  });

  if (response.data?.meal?.saveMeal.success) {
    return response;
  }

  throw new SentryGraphQLError();
};

const deleteItem = async (id: string, type: Delete_Type) => {
  const response = await graphqlClient.mutate({
    mutation: DeleteMealDocument,
    variables: {
      id,
      type,
    },
  });

  if (response.data?.meal?.deleteMeal?.success) {
    return response;
  }

  throw new SentryGraphQLError();
};

const downloadMealsAsExcel = async (
  token: string,
  institutionId: string,
  start: string,
  end: string,
  filename: string,
) => {
  const requestURL = new URL(APP_API_URL);
  requestURL.pathname = "ibg/institution/getMealParticipantsXlsx";
  requestURL.searchParams.set("id", institutionId);
  requestURL.searchParams.set("start_date", start);
  requestURL.searchParams.set("end_date", end);

  const response = await fetch(requestURL.toString(), {
    headers: {
      Authorization: token,
    },
  });

  if (!response.ok) {
    throw new Error("Download error");
  }

  const contentType = response.headers.get("content-type");
  if (
    !contentType?.includes("spreadsheet") &&
    !contentType?.includes("excel")
  ) {
    throw new Error("Invalid file type received");
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);

  // Create a temporary anchor element
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}.xlsx`;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export default {
  getMeals,
  getMealDetail,
  getExternalMeals,
  getExternalMealDetail,
  save,
  deleteItem,
  downloadMealsAsExcel,
};
